import React, { useState } from 'react';

// --- ICONS (for a real app, use an icon library or SVGs) ---
const PlusIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);
const Trash2Icon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
);
const SendIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
);
const SpinnerIcon = ({ className }) => (
    <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

// --- MOCK DATA ---
const initialWebhooks = [
    { id: 1, url: 'https://api.example.com/hooks/lead-updates', events: ['lead.created', 'lead.updated'], status: 'Active', lastDelivery: { status: 'Success', time: '2025-09-23T18:31:00.000Z' } },
    { id: 2, url: 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX', events: ['campaign.sent'], status: 'Active', lastDelivery: { status: 'Success', time: '2025-09-23T14:15:00.000Z' } },
    { id: 3, url: 'https://api.internal-tool.dev/data-ingest', events: ['contact.deleted'], status: 'Disabled', lastDelivery: { status: 'Failed', time: '2025-09-22T10:05:00.000Z' } },
];
const AVAILABLE_EVENTS = ['lead.created', 'lead.updated', 'contact.created', 'contact.deleted', 'campaign.sent'];

// --- UI COMPONENTS ---
const SectionCard = ({ children, title, subtitle }) => ( 
    <div>
        <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
        <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
        <div className="mt-6">{children}</div>
    </div>
);
const Card = ({ children, className }) => ( 
    <div className={`bg-white/70 backdrop-blur-sm p-0 rounded-2xl shadow-lg border border-white/50 overflow-hidden ${className}`}>
        {children}
    </div>
);
const EventBadge = ({ event }) => (
    <span className="px-2 py-1 text-xs font-mono bg-slate-100 text-slate-700 rounded">{event}</span>
);

const Webhooks = () => {
    const [webhooks, setWebhooks] = useState(initialWebhooks);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newUrl, setNewUrl] = useState('');
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const handleAddWebhook = (e) => {
        e.preventDefault();
        if (!newUrl || selectedEvents.length === 0) return;

        setIsSaving(true);
        setTimeout(() => {
            const newWebhook = {
                id: Date.now(),
                url: newUrl,
                events: selectedEvents,
                status: 'Active',
                lastDelivery: { status: 'Pending', time: null }
            };
            setWebhooks([newWebhook, ...webhooks]);
            setIsSaving(false);
            setShowAddForm(false);
            setNewUrl('');
            setSelectedEvents([]);
        }, 1000);
    };

    const handleEventToggle = (event) => {
        setSelectedEvents(prev => 
            prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]
        );
    };
    
    const formatTimestamp = (isoString) => {
        if (!isoString) return 'N/A';
        return new Date(isoString).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' });
    };

    return (
        <SectionCard title="Webhooks" subtitle="Configure webhooks to receive real-time updates for specific events.">
            <Card>
                {/* Header and Add Button */}
                <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-800">Endpoints ({webhooks.length})</h3>
                    <button onClick={() => setShowAddForm(!showAddForm)} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md flex items-center gap-2">
                        <PlusIcon className="w-5 h-5" />
                        Add Webhook
                    </button>
                </div>

                {/* Add Webhook Form */}
                {showAddForm && (
                    <div className="p-6 bg-slate-50 border-b border-slate-200">
                        <form onSubmit={handleAddWebhook} className="space-y-4">
                            <div>
                                <label htmlFor="webhookUrl" className="block text-sm font-medium text-slate-700">Endpoint URL</label>
                                <input type="url" id="webhookUrl" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://..." required className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm"/>
                            </div>
                             <div>
                                <label className="block text-sm font-medium text-slate-700">Events to send</label>
                                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {AVAILABLE_EVENTS.map(event => (
                                        <label key={event} className="flex items-center space-x-2 text-sm p-2 rounded-md hover:bg-slate-200 cursor-pointer">
                                            <input type="checkbox" checked={selectedEvents.includes(event)} onChange={() => handleEventToggle(event)} className="rounded"/>
                                            <span className="font-mono">{event}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-3">
                                <button type="button" onClick={() => setShowAddForm(false)} className="bg-white text-slate-700 font-semibold py-2 px-4 rounded-lg border border-slate-300 hover:bg-slate-50">Cancel</button>
                                <button type="submit" disabled={isSaving} className="bg-slate-800 text-white font-semibold py-2 px-4 rounded-lg hover:bg-slate-900 disabled:bg-slate-400 flex items-center justify-center min-w-[100px]">
                                    {isSaving ? <SpinnerIcon className="w-5 h-5"/> : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Webhooks List */}
                <div className="divide-y divide-slate-200">
                    {webhooks.map(webhook => (
                        <div key={webhook.id} className="p-6 space-y-3">
                            <div className="flex flex-col sm:flex-row justify-between gap-2">
                                <p className="font-mono text-slate-800 font-medium break-all">{webhook.url}</p>
                                <div className="flex items-center gap-4 flex-shrink-0">
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${webhook.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>{webhook.status}</span>
                                    <button className="text-slate-500 hover:text-slate-800"><SendIcon className="w-4 h-4" /></button>
                                    <button className="text-slate-500 hover:text-red-600"><Trash2Icon className="w-4 h-4" /></button>
                                </div>
                            </div>
                             <div className="flex flex-wrap gap-2">
                                {webhook.events.map(event => <EventBadge key={event} event={event} />)}
                            </div>
                            <div className="text-xs text-slate-500">
                                Last delivery: 
                                <span className={`font-medium ${webhook.lastDelivery.status === 'Success' ? 'text-green-600' : webhook.lastDelivery.status === 'Failed' ? 'text-red-600' : 'text-slate-600'}`}> {webhook.lastDelivery.status}</span>
                                {webhook.lastDelivery.time && ` at ${formatTimestamp(webhook.lastDelivery.time)}`}
                            </div>
                        </div>
                    ))}
                    {webhooks.length === 0 && (
                        <p className="text-center text-slate-500 p-10">
                            No webhooks configured.
                        </p>
                    )}
                </div>
            </Card>
        </SectionCard>
    );
};

export default Webhooks;
